import { useNetworkStatus } from '@airship-platform/airship-patterns';
import { PropsWithChildren, useCallback } from 'react';
import {
  AppointmentsContext,
  AppointmentsProvider,
  AppointmentsResponse,
  useAppointments,
} from '../../../data-access/appointments';
import { useOfflineProviderInit } from '../../rxdb/useOfflineProviderInit';
import { useOfflineDbData } from '../../rxdb/utils/OfflineDbProvider';
import RxDBSetupNotificationAlert from '../../rxdb/utils/RxDBSetupNotificationAlert';

const OfflineAppointmentsOverride: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const context = useAppointments();
  const data = useOfflineDbData();
  const isOnline = useNetworkStatus();

  const {
    upsertAppointments,
    removeAppointments,
    setSelectedAppointmentId,
    fetchAppointments: fetchAppointmentsContext,
    fetchAppointment: fetchAppointmentContext,
    fetchAppointmentsSkipped,
  } = context;

  const sync = useCallback(async () => {
    const docs = await data.find('appointments', {});
    docs.length && upsertAppointments(docs);
  }, [data, upsertAppointments]);

  const fetchAppointmentsCallback = useCallback(
    async (appointments: AppointmentsResponse[]) => {
      const appointmentIds = appointments.map((a) => a.appointmentId);
      if (appointmentIds.length) {
        const removedItems = await data.removeStale(
          'appointments',
          'appointmentId',
          appointmentIds
        );

        removeAppointments(removedItems.map((d) => d.appointmentId));
      }

      const existingAppointments = await data
        .find('appointments', {
          selector: { appointmentId: { $in: appointmentIds } },
        })
        .then((docs) => new Map(docs.map((d) => [d.appointmentId, d])));

      return data.bulkUpsert(
        'appointments',
        appointments.map((a) => {
          const existing = existingAppointments.get(a.appointmentId);
          return {
            ...a,
            job: {
              ...a.job,
              tagTypes: existing?.job.tagTypes || [],
              assets: existing?.job.assets || [],
              callSummaries: existing?.job.callSummaries || [],
            },
            customer: {
              ...a.customer,
              contacts: existing?.customer.contacts || [],
            },
            memberships: existing?.memberships || [],
            activities: existing?.activities || [],
          };
        })
      );
    },
    [data, removeAppointments]
  );

  const { initError } = useOfflineProviderInit(sync, sync);

  const fetchAppointments: typeof fetchAppointmentsContext = useCallback(
    (options) => {
      if (isOnline()) {
        return fetchAppointmentsContext(options).then(
          fetchAppointmentsCallback
        );
      }
      fetchAppointmentsSkipped();
      return Promise.resolve([]);
    },
    [
      fetchAppointmentsCallback,
      fetchAppointmentsContext,
      fetchAppointmentsSkipped,
      isOnline,
    ]
  );

  const fetchAppointment: typeof fetchAppointmentContext = useCallback(
    async (appointmentId, options) => {
      if (isOnline()) {
        return fetchAppointmentContext(appointmentId, options).then(
          (appointment) =>
            appointment
              ? data
                  .bulkUpsert('appointments', [appointment])
                  .then(([doc]) => doc)
              : undefined
        );
      }

      const doc = await data.findOne('appointments', appointmentId);
      if (doc) {
        upsertAppointments([doc]);
      }
      setSelectedAppointmentId(appointmentId);

      return doc ?? undefined;
    },
    [
      data,
      fetchAppointmentContext,
      isOnline,
      setSelectedAppointmentId,
      upsertAppointments,
    ]
  );

  if (initError) {
    return (
      <RxDBSetupNotificationAlert error={initError} entityName="appointments" />
    );
  }

  return (
    <AppointmentsContext.Provider
      value={{ ...context, fetchAppointments, fetchAppointment }}
    >
      {children}
    </AppointmentsContext.Provider>
  );
};

export const OfflineAppointmentsProvider: React.FC<PropsWithChildren> = ({
  children,
}) => (
  <AppointmentsProvider>
    <OfflineAppointmentsOverride>{children}</OfflineAppointmentsOverride>
  </AppointmentsProvider>
);
